import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Group } from '@mantine/core'

import { FaIcon } from '#components/FaIcon'
import { StripePricingTable } from '#components/Settings/Org/StripePricingTable'

import { ModalShell } from '../ModalShell'
import { useModalControls } from '../useModalControls'

import styles from './PricingTable.module.css'

// Stripe's pricing table has no "per seat" copy, so we
// overlay our own note. We detect render via the iframe's internal
// `iframeHeight` postMessage (undocumented, may change) and wait until the
// height stops changing so the note doesn't appear before the UI is painted.
const STRIPE_ORIGIN = 'https://js.stripe.com'
const SETTLE_MS = 600

const useStripeTableRendered = () => {
  const [rendered, setRendered] = React.useState(false)

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== STRIPE_ORIGIN || e.data?.id !== 'iframeHeight') return

      clearTimeout(timer)
      timer = setTimeout(() => setRendered(true), SETTLE_MS)
    }

    window.addEventListener('message', onMessage)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
    }
  }, [])

  return rendered
}

const PricingTable = ({
  customerSessionClientSecret,
}: {
  customerSessionClientSecret?: string
}) => {
  const controls = useModalControls()
  const { onClose } = controls
  const tableRendered = useStripeTableRendered()

  // close the modal when the user presses Back from Stripe Checkout
  React.useEffect(() => {
    const closeOnRestore = (e: PageTransitionEvent) => {
      if (e.persisted) onClose()
    }

    window.addEventListener('pageshow', closeOnRestore)
    return () => window.removeEventListener('pageshow', closeOnRestore)
  }, [onClose])

  return (
    <ModalShell
      title="Upgrade your workspace"
      size={1000}
      opened={controls.opened}
      onClose={controls.onClose}
    >
      <div className={styles.wrapper}>
        <StripePricingTable
          customerSessionClientSecret={customerSessionClientSecret}
        />
        {tableRendered && (
          <Group gap={8} wrap="nowrap" className={styles.note}>
            <FaIcon icon="fa-user-group" fz={12} />
            All prices are per user per month
          </Group>
        )}
      </div>
    </ModalShell>
  )
}

export const PricingTableModal = NiceModal.create(PricingTable)
